/**
 * `<caos-toast>` — a transient outcome notice. (Catalog: Overlays › Toast — a transient outcome notice.)
 *
 * Intents: info (default) · success · warning · danger, with `error` accepted as an
 * alias for danger to match the catalog's success/error/info naming. Intent colour
 * reads the SEMANTIC `--caos-color-*` tokens (decoupled from the brand accent). A
 * toast floats, so it carries `--caos-shadow-float` (the one allowed shadow).
 *
 * Auto-dismiss is EXPLICIT, never ambient: a timer runs only when a positive
 * `duration` (ms) is set AND the element is connected; it is cleared on disconnect
 * and on manual dismiss, and paused while hovered/focused so a reader is never raced.
 * On dismiss it fires a cancelable `caos-toast-dismiss`. An `action` slot carries an
 * optional undo. Styling seams: `::part(toast)`, `::part(icon)`, `::part(content)`,
 * `::part(dismiss)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { intentIcon, makeIcon } from './icons.js';

export type CaosToastIntent = 'info' | 'success' | 'warning' | 'danger';

const sheet = makeSheet(`
  :host { display: block; }
  :host([hidden]) { display: none !important; }

  .toast {
    display: flex;
    align-items: flex-start;
    gap: var(--caos-space-3);
    min-width: 240px;
    max-width: 420px;
    padding: var(--caos-space-3) var(--caos-space-4);
    border-radius: var(--caos-radius);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    box-shadow: var(--caos-shadow-float);   /* floats — the one allowed shadow */
    font-family: var(--caos-font);
  }

  /* a thin intent accent on the leading edge; body stays on surface for legibility */
  .toast { border-left: 3px solid var(--caos-color-info); }
  :host([intent="success"]) .toast { border-left-color: var(--caos-color-success); }
  :host([intent="warning"]) .toast { border-left-color: var(--caos-color-warning); }
  :host([intent="danger"]) .toast,
  :host([intent="error"]) .toast { border-left-color: var(--caos-color-danger); }

  .icon { flex: none; width: 18px; height: 18px; margin-top: 1px; color: var(--caos-color-info); }
  :host([intent="success"]) .icon { color: var(--caos-color-success); }
  :host([intent="warning"]) .icon { color: var(--caos-color-warning); }
  :host([intent="danger"]) .icon,
  :host([intent="error"]) .icon { color: var(--caos-color-danger); }
  .icon svg { display: block; width: 100%; height: 100%; }

  .content { flex: 1 1 auto; min-width: 0; font-size: 13px; line-height: 1.5; }
  .action { flex: none; display: flex; align-items: center; }

  .dismiss {
    flex: none;
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    width: 22px;
    height: 22px;
    padding: 3px;
    margin: -2px -4px -2px 0;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
  }
  .dismiss svg { display: block; width: 100%; height: 100%; }
  .dismiss:hover { color: var(--caos-color-text); background: color-mix(in srgb, currentColor 12%, transparent); }
  .dismiss:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* enter — slide + fade; suppressed under reduced-motion */
  .toast { animation: caos-toast-in 0.16s ease-out; }
  @keyframes caos-toast-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    .toast { animation: none; }
  }
`);

export class CaosToast extends CaosElement {
  static get observedAttributes(): string[] {
    return ['intent', 'duration'];
  }

  private toast: HTMLDivElement;
  private iconEl: HTMLSpanElement;
  private timer: number | null = null;

  constructor() {
    super(sheet);

    this.toast = document.createElement('div');
    this.toast.className = 'toast';
    this.toast.setAttribute('part', 'toast');
    this.toast.setAttribute('role', 'status');

    this.iconEl = document.createElement('span');
    this.iconEl.className = 'icon';
    this.iconEl.setAttribute('part', 'icon');
    this.iconEl.setAttribute('aria-hidden', 'true');

    const content = document.createElement('div');
    content.className = 'content';
    content.setAttribute('part', 'content');
    content.append(document.createElement('slot'));

    const action = document.createElement('div');
    action.className = 'action';
    const actionSlot = document.createElement('slot');
    actionSlot.name = 'action';
    action.append(actionSlot);

    const dismiss = document.createElement('button');
    dismiss.className = 'dismiss';
    dismiss.type = 'button';
    dismiss.setAttribute('part', 'dismiss');
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.append(makeIcon('close'));
    dismiss.addEventListener('click', () => this.dismiss());

    this.toast.append(this.iconEl, content, action, dismiss);
    this.root.append(this.toast);

    // Pause the auto-dismiss timer while the reader is engaged.
    this.toast.addEventListener('mouseenter', () => this.stopTimer());
    this.toast.addEventListener('focusin', () => this.stopTimer());
    this.toast.addEventListener('mouseleave', () => this.startTimer());
    this.toast.addEventListener('focusout', () => this.startTimer());
  }

  connectedCallback(): void {
    this.sync();
    this.startTimer();
  }

  disconnectedCallback(): void {
    this.stopTimer();
  }

  attributeChangedCallback(): void {
    this.sync();
    // Re-arm if the duration changed while connected.
    if (this.isConnected) {
      this.stopTimer();
      this.startTimer();
    }
  }

  /** Manually dismiss; cancelable via `caos-toast-dismiss`. */
  dismiss(): void {
    const ev = new CustomEvent('caos-toast-dismiss', { bubbles: true, composed: true, cancelable: true });
    if (this.dispatchEvent(ev)) this.remove();
  }

  private startTimer(): void {
    const raw = Number(this.getAttribute('duration'));
    if (!Number.isFinite(raw) || raw <= 0) return; // no ambient default — opt-in only
    this.stopTimer();
    this.timer = window.setTimeout(() => this.dismiss(), raw);
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private sync(): void {
    const intent = this.getAttribute('intent') ?? 'info';
    this.iconEl.replaceChildren(intentIcon(intent));
    this.toast.setAttribute('role', intent === 'danger' || intent === 'error' ? 'alert' : 'status');
  }
}

define('caos-toast', CaosToast);

declare global {
  interface HTMLElementTagNameMap {
    'caos-toast': CaosToast;
  }
}
